import React from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Divider } from "antd";
import MapClickHandler from "./MapClickHandler";
import { getMarkerIcon, yellowIcon, competitorIcon, partnerIcon } from "./markerIcons";
import { centerItalyPosition } from "./constants";

const MapView = ({ 
  filteredOrgCoords, 
  customLocation, 
  useCustomLocation, 
  isSelectingLocation, 
  proximityRange, 
  handleMapClick, 
  mapRef,
  showCompetitors,
  competitors,
  showPartners,
  partners
}) => {
  return (
    <MapContainer
      center={centerItalyPosition}
      zoom={6}
      style={{ height: "100%", width: "100%" }}
      scrollWheelZoom
      ref={mapRef}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="© OpenStreetMap contributors"
      />
      
      {/* Handler for map clicks */}
      <MapClickHandler 
        onMapClick={handleMapClick} 
        isSelectingLocation={isSelectingLocation} 
      />
      
      {/* Custom location marker */}
      {customLocation && useCustomLocation && (
        <Marker 
          position={[customLocation.lat, customLocation.lng]}
          icon={yellowIcon}
        >
          <Popup>
            <div>
              <strong>Punto di riferimento</strong><br />
              Posizione: {customLocation.lat.toFixed(6)}, {customLocation.lng.toFixed(6)}<br />
              Raggio: {proximityRange} km
            </div>
          </Popup>
        </Marker>
      )}
      
      {/* Partner markers */}
      {showPartners && partners && partners.map((partner, index) => (
        <Marker 
          key={`partner-${index}`} 
          position={[partner.lat, partner.lng]}
          icon={partnerIcon}
        >
          <Popup>
            <div>
              <strong style={{ background: 'linear-gradient(to right, red, orange, yellow, green, blue, indigo, violet)', '-webkit-background-clip': 'text', '-webkit-text-fill-color': 'transparent' }}>
                <span role="img" aria-label="partner">🤝</span> PARTNER
              </strong><br />
              <Divider style={{ margin: '8px 0' }} />
              <strong>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(partner.name)}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {partner.name}
                </a>
              </strong><br />
              <Divider style={{ margin: '8px 0' }} />
              <strong>Indirizzo:</strong><br />
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(partner.address)}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                {partner.address}
              </a>
              <Divider style={{ margin: '8px 0' }} />
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${partner.lat},${partner.lng}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Apri coordinate in Google Maps
              </a>
            </div>
          </Popup>
        </Marker>
      ))}
      
      {/* Competitor markers */}
      {showCompetitors && competitors && competitors.map((competitor, index) => (
        <Marker 
          key={`competitor-${index}`} 
          position={[competitor.lat, competitor.lng]}
          icon={competitorIcon}
        >
          <Popup>
            <div>
              <strong style={{ color: '#000000' }}>
                <span role="img" aria-label="competitor">⚠️</span> COMPETITOR
              </strong><br />
              <Divider style={{ margin: '8px 0' }} />
              <strong>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(competitor.name)}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {competitor.name}
                </a>
              </strong><br />
              <Divider style={{ margin: '8px 0' }} />
              <strong>Indirizzo:</strong><br />
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(competitor.address)}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                {competitor.address}
              </a>
              <Divider style={{ margin: '8px 0' }} />
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${competitor.lat},${competitor.lng}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Apri coordinate in Google Maps
              </a>
            </div>
          </Popup>
        </Marker>
      ))}
      
      {/* Organization markers */}
      {filteredOrgCoords.map((coord) => (
        <Marker 
          key={coord.objectId} 
          position={[coord.latitude, coord.longitude]}
          icon={getMarkerIcon(coord.org_pointer)}
        >
          <Popup>
            <div>
              <strong>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coord.org_pointer.nome)}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {coord.org_pointer.nome}
                </a>
              </strong><br />
              <span style={{ color: coord.org_pointer.stato === 'attiva' ? 'green' : 'red' }}>
                {coord.org_pointer.stato === 'attiva' ? 'Attiva' : 'Disattiva'}
              </span> - {coord.org_pointer.tipo_istanza}<br />
              
              {coord.org_pointer.indirizzo && (
                <>
                  <Divider style={{ margin: '8px 0' }} />
                  <strong>Indirizzo:</strong><br />
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      `${coord.org_pointer.indirizzo}, ${coord.org_pointer.cap} ${coord.org_pointer.citta}, ${coord.org_pointer.nazione || 'Italia'}`
                    )}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {coord.org_pointer.indirizzo}<br />
                    {coord.org_pointer.cap} {coord.org_pointer.citta}<br />
                    {coord.org_pointer.nazione || 'Italia'}
                  </a>
                </>
              )}
              <Divider style={{ margin: '8px 0' }} />
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${coord.latitude},${coord.longitude}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Apri coordinate in Google Maps
              </a>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default MapView; 